import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';
import { identifyUser, trackLogin, trackSignup, resetAnalytics } from '../lib/analytics';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('careerboost_token');
    const savedUser = localStorage.getItem('careerboost_user');
    
    if (token && savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      // Identify returning user for analytics
      identifyUser(userData.id, { email: userData.email, name: userData.name });
      // Verify token is still valid
      authAPI.getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('careerboost_user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('careerboost_token', access_token);
    localStorage.setItem('careerboost_user', JSON.stringify(userData));
    setUser(userData);
    
    // Track login for analytics
    identifyUser(userData.id, { email: userData.email, name: userData.name });
    trackLogin();
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register({ name, email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('careerboost_token', access_token);
    localStorage.setItem('careerboost_user', JSON.stringify(userData));
    setUser(userData);
    
    // Track signup for analytics
    identifyUser(userData.id, { email: userData.email, name: userData.name });
    trackSignup('registration_page');
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('careerboost_token');
    localStorage.removeItem('careerboost_user');
    setUser(null);
    resetAnalytics();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
